import { merge } from "lodash";
import { type FormValueEventInstance, type FormValues, RecordSubject } from "./types";

export const EVENT_DEFAULT_INSTANCE: FormValueEventInstance = {
    date: {
        year: null,
        month: null,
        day: null,
        endYear: null,
    },
    location: "",
    gpid: "",
    dateProximity: {
        year: null,
        month: null,
        day: null,
    },
    locationExactness: null,
};

export const DEFAULT_GIVEN_NAME_EXACTNESS: FormValues["name"]["givenNameExactness"] = {
    flags: {
        phonetic: false,
        similar: false,
        initials: false,
    },
    isExact: false,
};

export const DEFAULT_SURNAME_EXACTNESS: FormValues["name"]["surnameExactness"] = {
    flags: {
        phonetic: false,
        similar: false,
        soundex: false,
    },
    isExact: false,
};

export const RELATION_DEFAULT_INSTANCE: FormValues["name"] = {
    givenName: "",
    surname: "",
    secondSurname: "",
    givenNameExactness: DEFAULT_GIVEN_NAME_EXACTNESS,
    surnameExactness: DEFAULT_SURNAME_EXACTNESS,
};

const getRelationDefaultValue = (withDefaultInstances = false) => ({
    instances: withDefaultInstances ? [RELATION_DEFAULT_INSTANCE] : [],
});

const getEventDefaultValue = (withDefaultInstances = false) => ({
    instances: withDefaultInstances ? [EVENT_DEFAULT_INSTANCE] : [],
    relation: "",
    eventName: "",
});

export const getEmptyFormValues = (
    {
        withDefaultInstances,
        formValuePartials,
    }: {
        withDefaultInstances?: boolean;
        formValuePartials?: Partial<FormValues>;
    } = { withDefaultInstances: false },
): FormValues => {
    let formValues: FormValues = {
        name: {
            givenName: "",
            surname: "",
            secondSurname: "",
            givenNameExactness: {
                flags: {
                    phonetic: false,
                    similar: false,
                    initials: false,
                },
                isExact: false,
            },
            surnameExactness: {
                flags: {
                    phonetic: false,
                    similar: false,
                    soundex: false,
                },
                isExact: false,
            },
        },
        family: {
            father: getRelationDefaultValue(withDefaultInstances),
            mother: getRelationDefaultValue(withDefaultInstances),
            spouse: getRelationDefaultValue(withDefaultInstances),
            child: getRelationDefaultValue(withDefaultInstances),
            sibling: getRelationDefaultValue(withDefaultInstances),
        },
        events: {
            SelfBirth: getEventDefaultValue(withDefaultInstances),
            SelfMarriage: getEventDefaultValue(withDefaultInstances),
            SelfDeath: getEventDefaultValue(withDefaultInstances),
            SelfResidence: getEventDefaultValue(withDefaultInstances),
            SelfMilitary: getEventDefaultValue(withDefaultInstances),
            SelfDeparture: getEventDefaultValue(withDefaultInstances),
            SelfArrival: getEventDefaultValue(withDefaultInstances),
            SelfDivorce: getEventDefaultValue(withDefaultInstances),
            SelfBaptism: getEventDefaultValue(withDefaultInstances),
            SelfBurial: getEventDefaultValue(withDefaultInstances),
            Self: getEventDefaultValue(withDefaultInstances),
        },
        treePerson: {
            treeId: "",
            personId: "",
        },
        types: {
            trees: true,
            records: true,
            photos: true,
            stories: true,
        },
        priority: "default",
        location: [],
        fields: {},
        recordSubject: RecordSubject.All,
        showUnviewedRecordsOnly: false,
        timespan: {
            yearStart: null,
            yearEnd: null,
            matchMode: "rankOnly",
            isVisible: false,
        },
    };

    if (formValuePartials) {
        formValues = merge(formValues, formValuePartials);
    }

    return formValues;
};
